/**
 * Deterministic fabricators for the Implementation Partners page.
 * Numbers stay stable across refreshes because they're seeded by grantee name.
 * These supplement (do NOT replace) real backend metrics like sanctioned/utilised/# of grants.
 */

// xmur3 string -> 32-bit hash
function xmur3(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

// mulberry32 PRNG seeded by integer
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeRng(name) {
  const seed = xmur3(String(name || "partner"))();
  return mulberry32(seed);
}

const INDIAN_STATES = [
  "Maharashtra", "Karnataka", "Tamil Nadu", "Telangana", "Andhra Pradesh",
  "Kerala", "Gujarat", "Rajasthan", "Madhya Pradesh", "Uttar Pradesh",
  "Bihar", "Jharkhand", "Odisha", "West Bengal", "Assam",
  "Punjab", "Haryana", "Delhi", "Chhattisgarh", "Uttarakhand",
];

function pickStates(rng, count) {
  const pool = [...INDIAN_STATES];
  const out = [];
  for (let i = 0; i < count && pool.length; i++) {
    const idx = Math.floor(rng() * pool.length);
    out.push(pool.splice(idx, 1)[0]);
  }
  return out;
}

/**
 * Fabricate the per-card data that doesn't come from the backend.
 * Determined entirely from `grantee` name + grant count (for plausibility scaling).
 */
export function fabricatePartnerExtras(grantee, grantsCount = 1) {
  const rng = makeRng(grantee);

  // Beneficiaries: scale loosely with grant count, range 200 – 6500
  const baseBenef = 200 + Math.floor(rng() * 1800);
  const beneficiaries = baseBenef + grantsCount * (300 + Math.floor(rng() * 500));

  // States: 1–4 states
  const stateCount = 1 + Math.floor(rng() * 4);
  const states = pickStates(rng, stateCount);

  // Rating: 3.20 – 4.90
  const rating = Math.round((3.2 + rng() * 1.7) * 100) / 100;

  // KPI %: 55 – 95
  const kpiPct = Math.round(55 + rng() * 40);

  // Partnering since: 2015 – 2022
  const partneringSince = 2015 + Math.floor(rng() * 8);

  // Report status: deterministic bucket
  const reportDice = rng();
  let reportStatus;
  if (reportDice < 0.55) {
    reportStatus = { tone: "good", label: "All reports submitted" };
  } else if (reportDice < 0.8) {
    const n = 1 + Math.floor(rng() * Math.min(3, Math.max(1, grantsCount)));
    reportStatus = { tone: "warn", label: `UC overdue · ${n} grant${n > 1 ? "s" : ""}` };
  } else {
    const n = 1 + Math.floor(rng() * 2);
    reportStatus = { tone: "bad", label: `Report overdue · ${n} grant${n > 1 ? "s" : ""}` };
  }

  return { beneficiaries, states, rating, kpiPct, partneringSince, reportStatus };
}

/** Format beneficiary count as e.g. "2.1k" or "4.2k" or "920". */
export function fmtBeneficiaries(n) {
  if (n === null || n === undefined) return "–";
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
